import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/react-hooks";
import {
    Badge,
    Button,
    HeaderActions,
    Heading,
    LoadingProgress,
    Modal,
    Panel,
    Table,
    TableFooter,
    Text,
} from "@/ui";
import { useNotification } from "@/hooks";
import useMeta from "../../base/hooks/useMeta.js";
import {
    MUTATION_GENERATE_TEMP_PASS,
    QUERY_USER_LIST,
} from "../../apollo/module-operations/user";
import UserFormModal from "./UserFormModal";
import GenerateTempPasswordModal from "./GenerateTempPassWord.jsx";
import { PlusIcon, RowActionsMenu, storeTableUiStyles } from "../store/shared";
import styles from "../store/GeneralsForm.module.scss";
import deliveryStyles from "../store/DeliverySchedules.module.scss";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

const AccountUsers = () => {
    const { user, isCreate } = useMeta();
    const notify = useNotification();
    const [userId, setUserId] = useState(null);
    const [email, setEmail] = useState(null);
    const [showUserForm, setShowUserForm] = useState(false);
    const [newPassword, setNewPassword] = useState(null);
    const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const {
        data: stores,
        loading: storeLoading,
        networkStatus,
    } = useQuery(QUERY_USER_LIST, {
        variables: {
            account_id: user.accountId,
        },
        skip: isCreate || !user.accountId || !user.storeId,
    });

    const [mutate] = useMutation(MUTATION_GENERATE_TEMP_PASS, {
        onCompleted: resp => {
            setNewPassword(resp.generateTempPass);
            notify("Password Was Generete Successfully", "success");
        },
        onError: () => {
            notify("Updating error when generating the password", "error");
        },
    });

    const users = useMemo(() => (stores && stores.listUsers) || [], [stores]);

    const totalPages = Math.max(1, Math.ceil(users.length / pageSize) || 1);
    const currentPage = Math.min(page, totalPages);
    const pageStart = (currentPage - 1) * pageSize;
    const pageRows = useMemo(
        () => users.slice(pageStart, pageStart + pageSize),
        [users, pageStart, pageSize],
    );

    const launchGeneratePassword = rowEmail => {
        setEmail(rowEmail);
        setShowGenerateConfirm(true);
    };

    const confirmGeneratePassword = () => {
        setShowGenerateConfirm(false);
        mutate({
            variables: {
                input: {
                    email,
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
            },
        });
    };

    const createUser = () => {
        setUserId(null);
        setShowUserForm(true);
    };

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            items={[
                                {
                                    id: "update",
                                    label: "Edit",
                                    onSelect: () => {
                                        setUserId(row.id);
                                        setShowUserForm(true);
                                    },
                                },
                                {
                                    id: "generate-password",
                                    label: "Generate Password",
                                    onSelect: () =>
                                        launchGeneratePassword(row.email),
                                },
                            ]}
                            label={
                                `${row.first_name || ""} ${
                                    row.last_name || ""
                                }`.trim() ||
                                row.email ||
                                "user"
                            }
                        />
                    </span>
                ),
            },
            {
                id: "name",
                header: "Name",
                minWidth: 180,
                renderCell: row =>
                    `${row.first_name || ""} ${row.last_name || ""}`.trim(),
            },
            {
                id: "email",
                header: "Email",
                minWidth: 220,
                renderCell: row => row.email || "",
            },
            {
                id: "status",
                header: "Status",
                minWidth: 120,
                renderCell: row => (
                    <Badge variant={row.enabled ? "primary" : "neutral"}>
                        {row.enabled ? "Active" : "No Active"}
                    </Badge>
                ),
            },
        ],
        [],
    );

    const _storeLoading = storeLoading || networkStatus === 4;

    const createUserActions = [
        {
            id: "create-user",
            label: "Create User",
            icon: <PlusIcon />,
            onClick: createUser,
        },
    ];

    return (
        <>
            <Modal
                description={`Are you sure you want to generate the password for the user ${
                    email || ""
                } ?`}
                footer={
                    <div className={deliveryStyles.modalActions}>
                        <Button onClick={confirmGeneratePassword} type="button">
                            Generate Password
                        </Button>
                        <Button
                            onClick={() => setShowGenerateConfirm(false)}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) setShowGenerateConfirm(false);
                }}
                open={showGenerateConfirm}
                title="Generate Password"
            >
                <Text tone="secondary" variant="body-md">
                    A temporary password will be generated for this user.
                </Text>
            </Modal>

            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Users</Heading>
                            <Text tone="secondary" variant="body-md">
                                All Business Users
                            </Text>
                        </div>
                        <div className={styles.headerActions}>
                            <HeaderActions
                                actions={createUserActions}
                                aria-label="Users actions"
                            />
                        </div>
                    </div>
                }
                showHeaderDivider
            >
                {_storeLoading ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            indeterminate
                            indicator="circular"
                            size="sm"
                            title="Loading"
                        />
                    </div>
                ) : (
                    <>
                        <Table
                            columns={columns}
                            getRowId={row => String(row.id)}
                            rows={pageRows}
                        />
                        <TableFooter
                            onPageChange={setPage}
                            onPageSizeChange={size => {
                                setPageSize(size);
                                setPage(1);
                            }}
                            page={currentPage}
                            pageSize={pageSize}
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                            totalPages={totalPages}
                            totalRows={users.length}
                        />
                    </>
                )}
            </Panel>

            <UserFormModal
                isOpen={showUserForm}
                toggle={setShowUserForm}
                userId={userId}
            />
            <GenerateTempPasswordModal
                isOpen={newPassword != null}
                newPassword={newPassword}
                toggle={setNewPassword}
            />
        </>
    );
};

export default AccountUsers;
